<template>
  <div>
    <v-card class="pa-3">
      <v-layout wrap>
        <v-flex xs6>
          <h6>Ticket List</h6>
        </v-flex>
        <v-flex xs6 class="text-xs-right">
          <v-btn flat icon @click="$emit('close')">
            <v-icon>close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
      <div class="my-3">
        <date-filter
          v-model="selectedDate"
          :default-filter="'last_7_days'"
          @changeDate="handleFilterDate"
        />
      </div>
      <div>
        <v-data-table
          :headers="headers"
          :pagination.sync="pagination"
          :items="ticketList"
          item-key="id"
          class="elevation-1"
          hide-actions
          :loading="loading"
        >
          <!-- Name,Email,Phone,Channel , Note, Status, Assign to, Group ,Subgroup -->
          <template #items="props">
            <tr
              class="lighten-5"
              :class="{
                grey: props.item.status == 'closed',
                orange: props.item.status == 'pending',
                red: props.item.status == 'cancelled',
                purple: props.item.status == 'cancelled by customer'
              }"
            >
              <td>{{ props.item.id }}</td>
              <td>
                <v-chip v-if="props.item.priority == 'low'" color="yellow" small dark>{{
                  props.item.priority
                }}</v-chip>
                <v-chip v-else-if="props.item.priority == 'medium'" color="orange" small dark>{{
                  props.item.priority
                }}</v-chip>
                <v-chip v-else color="red" small dark>{{ props.item.priority }}</v-chip>
              </td>
              <td>{{ props.item.assignadminid }}</td>
              <td>
                <v-chip
                  v-if="props.item.status == 'pending'"
                  :color="'warning'"
                  small
                  disabled
                  class="white--text"
                  >{{ props.item.status }}</v-chip
                >
                <v-chip
                  v-else-if="props.item.status == 'closed'"
                  :color="'grey'"
                  small
                  disabled
                  class="white--text"
                  >{{ props.item.status }}</v-chip
                >
                <v-chip
                  v-else-if="props.item.status == 'success'"
                  :color="'grey'"
                  small
                  disabled
                  class="white--text"
                  >{{ props.item.status }}</v-chip
                >
                <v-chip
                  v-else-if="props.item.status == 'cancelled'"
                  :color="'error'"
                  small
                  disabled
                  class="white--text"
                  >{{ props.item.status }}</v-chip
                >
                <v-chip
                  v-else-if="props.item.status == 'cancelled by customer'"
                  :color="'#fd87ee'"
                  small
                  disabled
                  class="white--text"
                  >cancelled by customer</v-chip
                >
                <v-chip
                  v-else-if="props.item.status == 'answered'"
                  :color="'info'"
                  small
                  disabled
                  class="white--text"
                  >{{ props.item.status }}</v-chip
                >
                <v-chip
                  v-else-if="props.item.status == 'open'"
                  :color="'green'"
                  small
                  disabled
                  class="white--text"
                  >{{ props.item.status }}</v-chip
                >
                <v-chip v-else :color="'info'" small disabled class="white--text">{{
                  props.item.status
                }}</v-chip>
              </td>

              <td>{{ props.item.contact_firstname }} {{ props.item.contact_lastname }}</td>
              <td style="white-space: nowrap">
                <div>{{ remainingTime(props.item.duedate) }}</div>
              </td>
              <td style="min-width: 300px">{{ props.item.subject }}</td>
              <td style="min-width: 300px">{{ props.item.details }}</td>
              <td>
                <template v-if="props.item.starttime">{{
                  props.item.starttime | DateFormat('DD/MM/YYYY HH:mm')
                }}</template>
              </td>

              <td>
                <template v-if="props.item.adminfirstresponsetime">{{
                  props.item.adminfirstresponsetime | DateFormat('DD/MM/YYYY HH:mm')
                }}</template>
              </td>

              <td>
                <template v-if="props.item.endtime">{{
                  props.item.endtime | DateFormat('DD/MM/YYYY HH:mm')
                }}</template>
              </td>
              <td>
                <template v-if="props.item.starttime && props.item.endtime">{{
                  addDurationUnit(durationTime(props.item.starttime, props.item.endtime))
                }}</template>
              </td>
              <td>
                <template v-if="props.item.duedate">{{
                  props.item.duedate | DateFormat('DD/MM/YYYY HH:mm')
                }}</template>
              </td>
              <td style="min-width: 300px">{{ props.item.tags }}</td>
              <td>
                {{
                  groups[props.item.assigngroupid]
                    ? groups[props.item.assigngroupid].groupname
                    : '-'
                }}
              </td>
              <td>
                {{
                  subgroups[props.item.assignsubgroupid]
                    ? subgroups[props.item.assignsubgroupid].subgroupname
                    : '-'
                }}
              </td>
              <td>
                <span v-if="props.item.channel == 'facebook'">
                  <em style="color: rgb(59, 89, 152)" class="fab fa-facebook-square" />
                </span>
                <span v-else-if="props.item.channel == 'line'">
                  <em style="color: rgb(0, 195, 0)" class="fab fa-line" />
                </span>
                <span v-else-if="props.item.channel == 'app'">
                  <em style="color: rgb(59, 89, 152)" class="fas fa-mobile-alt" />
                </span>
                <span v-else-if="props.item.channel == 'webchat'">
                  <em style="color: rgb(0, 195, 0)" class="fas fa-globe" />
                </span>
                <span v-else><em style="color: grey" class="fas fa-envelope" /></span>
              </td>
              <td>{{ props.item.contact_email }}</td>
              <td>{{ props.item.contact_phone }}</td>
            </tr>
          </template>
        </v-data-table>

        <div class="text-xs-center pt-2">
          <v-pagination
            v-model="pageNumber"
            :total-visible="15"
            :length="pageLength"
            @input="handleChangePage"
          ></v-pagination>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import ticketApi from '@/api/ticket.js'
import { mapGetters } from 'vuex'
import DateFilter from '@/components/view/dashboard/DateFilter.vue'
import duration from 'dayjs/plugin/duration'
import api from '@/api'

dayjs.extend(duration)

export default {
  components: {
    DateFilter
  },
  props: {
    item: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      loading: true,
      limit: 10,
      offset: 0,
      pageTemp: 1,
      pageNumber: 1,
      pagination: { rowsPerPage: -1, sortBy: '' },
      total: 0,
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Priority', value: 'priority' },
        { text: 'Assign To', value: 'assignadminid' },
        { text: 'Status', value: 'status' },
        { text: 'Name', value: 'first_name' },
        { text: 'Remaining Time', value: 'remainingtime' },
        { text: 'Subject', value: 'subject' },
        { text: 'Details', value: 'details' },
        { text: 'Start time', value: 'starttime' },
        { text: 'Admin First Time Response', value: 'adminfirstresponsetime' },
        { text: 'End Time', value: 'endtime' },
        { text: 'Time Closed', value: 'timeclosed' },
        { text: 'Due Date', value: 'duedate' },
        { text: 'Tags', value: 'tags' },
        { text: 'Group', value: 'assigngroupid' },
        { text: 'Subgroup', value: 'assignsubgroupid' },
        { text: 'Channel', value: 'channel' },
        { text: 'Email', value: 'email' },
        { text: 'Phone', value: 'phoneno' }
      ],
      selectedDate: {
        start: dayjs().subtract(7, 'days').format('YYYY-MM-DD'),
        end: dayjs().format('YYYY-MM-DD')
      },
      ticketList: [],
      groups: {},
      subgroups: {}
    }
  },
  computed: {
    ...mapGetters({
      selectedBot: 'selectedBot'
    }),
    pageLength() {
      if (this.total) {
        let total = parseFloat(this.total)
        return Math.ceil(total / this.limit)
      } else {
        return 1
      }
    }
  },
  mounted() {
    if (this.item) {
      this.handleInit()
    }
  },
  methods: {
    async handleInit() {
      const vm = this
      try {
        const responseGroup = await vm.getAdminGroup()
        const responseSubGroup = await vm.getAdminSubGroup()
        if (responseGroup.data) {
          let group = {}
          responseGroup.data.map((el) => {
            group[el.id] = el
          })
          vm.groups = group
        }
        if (responseSubGroup.data) {
          let subgroup = {}
          responseSubGroup.data.map((el) => {
            subgroup[el.id] = el
          })
          vm.subgroups = subgroup
        }

        this.handleFetchTicketByReason()
      } catch (error) {
        console.error(error)
      }
    },
    async handleFetchTicketByReason() {
      const vm = this
      try {
        vm.loading = true
        const data = {
          page_id: vm.selectedBot.pageid,
          date_from: vm.selectedDate.start,
          date_to: vm.selectedDate.end,
          reason: vm.item.reason,
          level: vm.item.level,
          limit: vm.limit,
          offset: vm.offset
        }

        const response = await ticketApi.getTicketWithReasonLevel({
          config: {
            params: data
          }
        })
        vm.loading = false

        if (response && response.status === 200) {
          const responseData = response.data.data
          vm.ticketList = responseData
          vm.total = response.data.total
        }
      } catch (error) {
        console.error(error)
      } finally {
        vm.loading = false
      }
    },
    handleFilterDate() {
      this.offset = 0
      this.pageNumber = 1
      this.handleFetchTicketByReason()
    },
    handleChangePage(val) {
      const vm = this
      if (vm.pageTemp !== val) {
        vm.pageTemp = val
        vm.offset = vm.pageNumber * vm.limit - vm.limit
        if (vm.pageNumber != 1) {
          vm.offset + 1
        }
        vm.handleFetchTicketByReason()
      }
    },
    durationTime(start, end) {
      const startTime = start
      const endTime = end
      const minutes = dayjs
        .duration(dayjs(endTime, 'YYYY/MM/DD HH:mm').diff(dayjs(startTime, 'YYYY/MM/DD HH:mm')))
        .asMinutes()
        .toFixed(2)

      return minutes
    },
    addDurationUnit(minutes) {
      return dayjs.duration(parseFloat(minutes), 'minutes').format('H [hrs], m [min]')
    },
    remainingTime(end) {
      const startTime = dayjs()
      const endTime = dayjs(end)
      if (startTime.isBefore(endTime)) {
        const minutes = dayjs.duration(endTime.diff(startTime)).asMinutes().toFixed(2)
        return dayjs.duration(parseFloat(minutes), 'minutes').format('H [hrs], m [min]')
      } else {
        return 0
      }
    },
    getAdminGroup() {
      const pageid = this.selectedBot.pageid

      return new Promise((resolve, reject) => {
        api.getAdminGroup({ pageid }, (error, response) => {
          if (error) {
            console.error(error)
            reject(error)
          }
          resolve(response)
        })
      })
    },
    getAdminSubGroup() {
      const pageid = this.selectedBot.pageid

      return new Promise((resolve, reject) => {
        api.getAdminSubGroup({ pageid }, (error, response) => {
          if (error) {
            console.error(error)
            reject(error)
          }
          resolve(response)
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped></style>



// WEBPACK FOOTER //
// src/components/view/dashboard/customize/nt/Reason/DialogTicketByReason.vue